"use client";

import { Button } from "@components/base/buttons/button";
import Link from "next/link";
import { useEffect, useState } from "react";
import { Heading } from "react-aria-components";

import { Dialog, Modal, ModalOverlay } from "@/components/application/modals/modal";
import { CloseButton } from "@/components/base/buttons/close-button";
import { capturePosthogEvent } from "@/lib/posthog-browser";
import { useAuth } from "@/providers/auth";

const DISMISS_KEY_PREFIX = "partner_access_expired_modal_dismissed";

function dismissKeyForUser(userId: string) {
	return `${DISMISS_KEY_PREFIX}:${userId}`;
}

export function PartnerAccessExpiredModal() {
	const { user, isSessionResolving } = useAuth();
	const [open, setOpen] = useState(false);

	useEffect(() => {
		if (isSessionResolving || !user) {
			setOpen(false);
			return;
		}

		const { partnerBundleExpired, hasStripeSubscription } = user.membership;
		if (!partnerBundleExpired || hasStripeSubscription) {
			setOpen(false);
			return;
		}

		try {
			const dismissed = sessionStorage.getItem(dismissKeyForUser(user.id));
			const shouldOpen = dismissed !== "1";
			setOpen(shouldOpen);
			if (shouldOpen) {
				void capturePosthogEvent("partner_access_expired_modal_shown", { source: "modal" });
			}
		} catch {
			setOpen(true);
			void capturePosthogEvent("partner_access_expired_modal_shown", { source: "modal" });
		}
	}, [isSessionResolving, user]);

	if (!user?.membership.partnerBundleExpired || user.membership.hasStripeSubscription) {
		return null;
	}

	const dismiss = () => {
		try {
			sessionStorage.setItem(dismissKeyForUser(user.id), "1");
		} catch {
			// Private browsing / disabled storage — still close for this view.
		}
		setOpen(false);
	};

	return (
		<ModalOverlay
			isOpen={open}
			onOpenChange={(nextOpen) => {
				if (!nextOpen) dismiss();
			}}
			isDismissable
		>
			<Modal className="max-w-lg">
				<Dialog className="relative w-full flex-col rounded-xl bg-primary p-6 shadow-xl ring-1 ring-secondary_alt">
					<CloseButton
						slot="close"
						theme="light"
						size="md"
						className="absolute top-4 right-4 z-10"
						onPress={dismiss}
					/>
					<Heading slot="title" className="pr-10 text-lg font-semibold text-secondary">
						Your included Pro access has ended
					</Heading>
					<p className="mt-2 text-sm text-tertiary">
						Your complimentary Unilytics Pro access has expired. You can still sign in and use
						free-tier features, but Pro tools like extended pool performance, the range analyzer,
						and expert recommendations are no longer available.
					</p>
					<p className="mt-3 text-sm text-tertiary">Upgrade anytime to restore full Pro access.</p>
					<div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-end">
						<Button
							type="button"
							color="secondary"
							size="md"
							className="w-full sm:w-auto"
							onClick={dismiss}
						>
							Continue on free plan
						</Button>
						<Button
							type="button"
							color="primary"
							size="md"
							className="w-full sm:w-auto"
							onClick={() => {
								void capturePosthogEvent("partner_access_upgrade_clicked", { source: "modal" });
								dismiss();
								window.location.href = "/plans";
							}}
						>
							Upgrade to Pro
						</Button>
					</div>
					<p className="mt-4 text-center text-xs text-tertiary sm:text-left">
						Prefer to browse first?{" "}
						<Link
							href="/pools"
							className="font-medium text-brand-secondary hover:underline"
							onClick={dismiss}
						>
							Go to pools
						</Link>
					</p>
				</Dialog>
			</Modal>
		</ModalOverlay>
	);
}
